import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { CreateDocumentInput, Document, UpdateDocumentInput } from '@app/core/model/entities/document/document';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { eq } from '@app/shared/extra/filter-model-builder';
import { getLocale } from '@app/shared/extra/utils';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject, switchMap } from 'rxjs';
import { map, reduce, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class DocumentsService {
  private apiService = inject(ApiService);
  private appManager = inject(AppManager);
  private assetsService = inject(AssetsService);
  private appConfig = inject(AppConfig);

  private sidePanelToggleSubject = new Subject<Document | null>();
  private addDocumentsSubject = new Subject<Document[]>();
  private updateDocumentSubject = new Subject<Document>();
  private deleteDocumentsSubject = new Subject<Document[]>();

  private readonly documentInfoGraphqlFragment = gql`
    fragment DocumentInfo on Document {
      id
      name
      entityId
      entityType
      creationDate
      type
      state
      properties
      mimeType
      computedProperties
    }
  `;

  /**
   * Emits Documents after they have been added.
   */
  public get documentsAdded$(): Observable<Document[]> {
    return this.addDocumentsSubject.asObservable();
  }

  /**
   * Emit a Document after it has been updated.
   */
  public get documentUpdated$(): Observable<Document> {
    return this.updateDocumentSubject.asObservable();
  }

  /**
   * Emits Documents after they have been deleted.
   */
  public get documentsDeleted$(): Observable<Document[]> {
    return this.deleteDocumentsSubject.asObservable();
  }

  /**
   * Emits a Document whenever the side panel opens, or null whenever it closes.
   */
  public get sidePanelToggle$(): Observable<Document | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Loads a document's page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch documents based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the documents.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Document objects, each with additional user information.
   */
  public loadDocumentsPage(selectParams: SelectParams): Observable<Document[]> {
    const query = gql`
      query Documents($organizationId: String!, $selectParams: SelectParams!) {
        documents(organizationId: $organizationId, selectParams: $selectParams) {
          ...DocumentInfo
        }
      }
      ${this.documentInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Document, data['documents'] as Document[])));
  }

  /**
   * Loads all document pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch documents based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the documents.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Document objects, each with additional user information.
   */
  public loadAllDocuments(selectParams: SelectParams): Observable<Document[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadDocumentsPage(params)
      .pipe(
        expand((documents, i) => {
          if (documents.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadDocumentsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Document[])
      );
  }

  /**
   * Emits the categories that can be associated with new Documents for the current Organization.
   */
  public get availableDocumentCategories$(): Observable<string[]> {
    const selectParams: SelectParams = {
      locale: getLocale(),
      sorts: [{field: 'properties.documentCategory', direction: 'ASC'}],
      groups: ['properties.documentCategory']
    };

    return this.loadAllDocuments(selectParams).pipe(
      switchMap((documents) => from(documents)),
      map(document => document.properties.documentCategory),
      toArray()
    );
  }

  /**
   * Fetch Documents related to an entity.
   * @param entityId ID of the entity which is related to the Documents to fetch.
   * @param entityType Type of the entity.
   * @param documentType Type of Documents to return.
   */
  public loadEntityDocuments(
    entityId: string,
    entityType: EntityTypeEnum,
    documentType: DocumentType
  ): Observable<Document[]> {
    const selectParams = {
      filters: [
        eq('entityId', entityId),
        eq('entityType', entityType),
        eq('type', documentType)
      ]
    };

    return this.loadAllDocuments(selectParams);
  }

  /**
   * Return the document by its ID.
   * @param id Document ID
   * @return Document
   */
  public document(id: string): Observable<Document> {
    const query = gql`
      query Document($id: String!) {
        document(id: $id) {
          ...DocumentInfo
        }
      }
      ${this.documentInfoGraphqlFragment}
    `;
    const variables = {id};

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Document, data['document'])));
  }

  /**
   * Call the API to create Documents with the provided data.
   * @param documentInputs Data for creating new Documents.
   * @return Created Documents.
   */
  public createDocuments(documentInputs: CreateDocumentInput[]): Observable<Document[]> {
    const mutation = gql`
      mutation CreateDocuments($documentInputs: [CreateDocumentInput!]!) {
        createDocuments(documentInputs: $documentInputs) {
          ...DocumentInfo
        }
      }
      ${this.documentInfoGraphqlFragment}
    `;
    const variables = {documentInputs};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Document, data['createDocuments'] as Document[])),
        tap(newDocuments => this.addDocumentsSubject.next(newDocuments))
      );
  }

  /**
   * Call the API to update a Document.
   * @param document Document to update
   * @param documentInput Data for updating the Document.
   * @return Observable emitting the updated Document once the update is completed.
   */
  public updateDocument(document: Document, documentInput: UpdateDocumentInput): Observable<Document> {
    const mutation = gql`
      mutation UpdateOrganizationDocument($documentInput: UpdateDocumentInput!) {
        updateDocument(documentInput: $documentInput) {
          ...DocumentInfo
        }
      }
      ${this.documentInfoGraphqlFragment}
    `;

    if (documentInput.entityId) {
      // Entity should be an asset
      documentInput.entityType = EntityTypeEnum.ASSET;
    }

    const variables = {
      documentInput: {
        id: document.id,
        name: document.name,
        type: document.type,
        state: document.state,
        ...documentInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Document, data['updateDocument'])),
        tap(updatedDocument => this.updateDocumentSubject.next(updatedDocument))
      );
  }

  /**
   * Call the API to update Documents.
   * @param documents List of Documents to update.
   * @return Observable emitting the updated Documents once the update is completed.
   */
  public updateDocuments(documents: { document: Document, documentInput: UpdateDocumentInput }[]): Observable<Document[]> {
    const mutation = gql`
      mutation UpdateDocuments($documentInputs: [UpdateDocumentInput!]!) {
        updateDocuments(documentInputs: $documentInputs) {
          ...DocumentInfo
        }
      }
      ${this.documentInfoGraphqlFragment}
    `;
    const variables = {
      documentInputs: documents.map(({document, documentInput}) => {
        return {
          id: document.id,
          entityId: document.entityId,
          entityType: document.entityType,
          name: document.name,
          type: document.type,
          state: document.state,
          ...documentInput
        };
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Document, data['updateDocuments'] as Document[])),
        switchMap(documents => from(documents)),
        tap(updateDocument => this.updateDocumentSubject.next(updateDocument)),
        toArray()
      );
  }

  /**
   * Call the API to link a Document to an entity.
   * @param entityId ID of entity to link the document to.
   * @param entityType Type of the entity to link the document to.
   * @param documentId ID of the document to link.
   * @return Observable emitting the created Document once the document link is completed.
   */
  public linkDocumentToEntity(entityId: string, entityType: EntityTypeEnum, documentId: string): Observable<Document> {
    const mutation = gql`
      mutation LinkDocumentToEntity($entityId: String!, $entityType: EntityType!, $documentId: String!) {
        linkDocument(entityId: $entityId, entityType: $entityType, id: $documentId) {
          ...DocumentInfo
        }
      }
      ${this.documentInfoGraphqlFragment}
    `;
    const variables = {entityId, entityType, documentId};
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Document, data['linkDocument'])),
        tap(createdDocument => this.addDocumentsSubject.next([createdDocument]))
      );
  }

  /**
   * Call the API to delete a Document.
   * @param document Document to delete.
   * @return Observable that will emit a boolean value indicating whether the Document was deleted successfully.
   */
  public deleteEntityDocument(document: Document): Observable<boolean> {
    const mutation = gql`
      mutation DeleteDocument($documentId: String!) {
        deleteDocument(id: $documentId)
      }
    `;
    const variables = {documentId: document.id};

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => data['deleteDocument'] as boolean));
  }

  /**
   * Call the API to delete multiple Documents.
   * @param documents Documents to be deleted.
   * @return Observable that will emit a boolean value indicating whether the Documents were deleted successfully.
   */
  public deleteOrganizationDocuments(documents: Document[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteOrganizationDocuments($documentIds: [String!]!) {
        deleteDocuments(ids: $documentIds)
      }
    `;
    const variables = {
      documentIds: documents.map((selectedDocument) => selectedDocument.id)
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteDocuments'] as boolean),
        tap(success => success && this.deleteDocumentsSubject.next(documents))
      );
  }

  /**
   * Call the API to update Documents' states to archived.
   * @param documents Documents to archive.
   * @return Updated Documents.
   */
  public archiveOrganizationDocuments(documents: Document[]): Observable<Document[]> {
    const mutation = gql`
      mutation ArchiveDocuments($documentIds: [String!]!) {
        archiveDocuments(ids: $documentIds) {
          ...DocumentInfo
        }
      }
      ${this.documentInfoGraphqlFragment}
    `;
    const variables = {
      documentIds: documents.map((document) => document.id)
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Document, data['archiveDocuments'] as Document[])),
        tap(updatedDocuments => updatedDocuments.forEach((document) => this.updateDocumentSubject.next(document)))
      );
  }

  /**
   * Open the side panel displaying the Document.
   * @param document Document to be displayed in the side panel.
   */
  public openDocumentSidePanel(document: Document): void {
    this.sidePanelToggleSubject.next(document);
  }

  /**
   * Close the side panel.
   */
  public closeDocumentSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }

  /**
   * Navigate to the Asset's sheet and open the tab corresponding to the provided Document type.
   * ASSET_PICTURE will open the pictures tab and any other DocumentType will open the documents tab.
   * @param documentType Type of Document on which the tab to open depends.
   * @param assetId ID of the Asset whose sheet to open.
   */
  public async navigateToAssetSheet(documentType: DocumentType, assetId: string): Promise<void> {
    const tabName = documentType === DocumentType.PICTURE ? 'pictures' : 'documents';
    await this.assetsService.navigateToAssetSheet(assetId, tabName);
  }
}
